import { Paperclip } from 'lucide-react'
import { useState } from 'react'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { setStageStatus } from '@/data/stageApi'
import type { FileRecord } from '@/data/useProjectFiles'
import { errorMessage } from '@/lib/errors'
import { canEditStageStatus, canUploadStage } from '@/lib/permissions'
import { PROOF_STAGE_ID, type StageDefinition } from '@/lib/stages'
import type { LatLng, StageStatus } from '@/types/project'
import type { Role } from '@/types/user'
import { FileList } from './FileList'
import { FileUpload } from './FileUpload'

interface StageCardProps {
  projectId: string
  stage: StageDefinition
  status: StageStatus
  files: FileRecord[]
  role: Role
  user: { uid: string; name: string }
  /** Expected route points, for proof-of-product location checks. */
  expectedPoints?: LatLng[]
}

const STATUS_OPTIONS: {
  value: StageStatus
  label: string
  ariaLabel: string
  active: string
}[] = [
  {
    value: 'green',
    label: 'Complete',
    ariaLabel: 'Mark complete',
    active: 'bg-stage-green text-white',
  },
  {
    value: 'red',
    label: 'Incomplete',
    ariaLabel: 'Mark incomplete',
    active: 'bg-stage-red text-white',
  },
  {
    value: 'na',
    label: 'N/A',
    ariaLabel: 'Mark not applicable',
    active: 'bg-slate-500 text-white',
  },
]

export function StageCard({
  projectId,
  stage,
  status,
  files,
  role,
  user,
  expectedPoints,
}: StageCardProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canEdit = canEditStageStatus(role)
  const canUpload = canUploadStage(role, stage.id)
  const isProof = stage.id === PROOF_STAGE_ID

  async function setStatus(next: StageStatus) {
    if (next === status) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      await setStageStatus(projectId, stage.id, next, user.uid)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-medium text-slate-800">{stage.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusBadge status={status} />
            {files.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                <Paperclip className="h-3 w-3" />
                {files.length} file{files.length === 1 ? '' : 's'} uploaded
              </span>
            )}
          </div>
        </div>
        {canEdit && (
          <div
            role="group"
            aria-label="Set stage status"
            className="flex shrink-0 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
          >
            {STATUS_OPTIONS.map((option) => {
              const isActive = status === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`stage-${option.value}-${stage.id}`}
                  aria-label={option.ariaLabel}
                  aria-pressed={isActive}
                  disabled={busy}
                  onClick={() => setStatus(option.value)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition disabled:opacity-60 ${
                    isActive ? option.active : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {isProof && (
        <p className="mt-2 text-xs text-slate-500">
          Upload a photo or video of the goods, or a supporting document.
        </p>
      )}

      <div className="mt-3 space-y-3">
        <FileList
          projectId={projectId}
          files={files}
          role={role}
          currentUid={user.uid}
          proof={isProof}
          expectedPoints={expectedPoints}
        />
        {canUpload &&
          (isProof ? (
            <div className="flex flex-wrap gap-2">
              <FileUpload
                projectId={projectId}
                stage={stage.id}
                uploader={{ uid: user.uid, name: user.name, role }}
                variant="camera"
              />
              <FileUpload
                projectId={projectId}
                stage={stage.id}
                uploader={{ uid: user.uid, name: user.name, role }}
                variant="file"
                label="Upload document"
              />
            </div>
          ) : (
            <FileUpload
              projectId={projectId}
              stage={stage.id}
              uploader={{ uid: user.uid, name: user.name, role }}
              variant="file"
            />
          ))}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  )
}
